import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as userApi from '../services/user.service';
import * as activityApi from '../services/activity.service';
import * as paymentApi from '../services/payment.service';
import * as securityApi from '../services/security.service';
import * as entitlementApi from '../services/entitlement.service';
import type { UserListQuery } from '../types';

export const userKeys = {
  all: ['admin', 'users'] as const,
  list: (q: UserListQuery) => [...userKeys.all, 'list', q] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
  activity: (id: string, q: object) => [...userKeys.all, 'activity', id, q] as const,
  payments: (id: string, q: object) => [...userKeys.all, 'payments', id, q] as const,
  sessions: (id: string) => [...userKeys.all, 'sessions', id] as const,
  entitlement: (id: string) => [...userKeys.all, 'entitlement', id] as const,
  publishHistory: (id: string) => [...userKeys.all, 'publish-history', id] as const,
};

export const useUsersList = (q: UserListQuery) =>
  useQuery({ queryKey: userKeys.list(q), queryFn: () => userApi.listUsers(q) });

export const useUserDetail = (id: string | undefined) =>
  useQuery({
    queryKey: userKeys.detail(id ?? ''),
    queryFn: () => userApi.getUser(id as string),
    enabled: !!id,
  });

export const useUserActivity = (
  id: string,
  q: { page?: number; limit?: number; action?: string; from?: string; to?: string },
) =>
  useQuery({
    queryKey: userKeys.activity(id, q),
    queryFn: () => activityApi.listActivity(id, q),
    enabled: !!id,
  });

export const useUserPayments = (
  id: string,
  q: { page?: number; limit?: number; status?: string; refundedOnly?: boolean },
) =>
  useQuery({
    queryKey: userKeys.payments(id, q),
    queryFn: () => paymentApi.listUserPayments(id, q),
    enabled: !!id,
  });

export const useUserSessions = (id: string) =>
  useQuery({
    queryKey: userKeys.sessions(id),
    queryFn: () => securityApi.listSessions(id),
    enabled: !!id,
  });

export function useInvalidateUser(id?: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: userKeys.all });
    if (id) qc.invalidateQueries({ queryKey: userKeys.detail(id) });
  };
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => userApi.suspendUser(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useRestoreUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userApi.restoreUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useChangeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => userApi.changeUserRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useBulkSuspend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      userApi.bulkSuspend(ids, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useBulkRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => userApi.bulkRestore(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useForceLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => securityApi.forceLogout(id),
    onSuccess: (_d, id) => qc.invalidateQueries({ queryKey: userKeys.sessions(id) }),
  });
}

export function useGenerateResetLink() {
  return useMutation({ mutationFn: (id: string) => securityApi.generateResetLink(id) });
}

export function useRefundPayment(userId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { paymentId: string; amount?: number; reason: string }) =>
      paymentApi.refundPayment(input),
    onSuccess: () => {
      if (userId) qc.invalidateQueries({ queryKey: [...userKeys.all, 'payments', userId] });
    },
  });
}

// ── Publish entitlement / credits (beta gate) ───────────────────────────────
//
// staleTime: 0 + refetchOnWindowFocus: true ensure the admin always sees
// the live credit balance. Without these, React Query caches the entitlement
// for ~5 minutes — the admin opens the drawer, the user publishes, the admin
// re-focuses the window and still sees the old (pre-publish) credit count.
export const useUserEntitlement = (id: string | undefined) =>
  useQuery({
    queryKey: userKeys.entitlement(id ?? ''),
    queryFn: () => entitlementApi.getEntitlement(id as string),
    enabled: !!id,
    staleTime: 0,              // always re-fetch; credit counts change on every publish
    refetchOnWindowFocus: true, // catch publishes that happened in another tab/window
  });

export const useUserPublishHistory = (id: string | undefined) =>
  useQuery({
    queryKey: userKeys.publishHistory(id ?? ''),
    queryFn: () => entitlementApi.listPublishHistory(id as string),
    enabled: !!id,
    staleTime: 0,              // always fresh — history grows on every publish
    refetchOnWindowFocus: true,
  });

// Grant/revoke share the same invalidation: refresh the entitlement summary,
// the consumption history, and the user detail (which carries publishCredits).
function useCreditMutation(
  userId: string,
  fn: (id: string, input: { amount: number; reason?: string }) => Promise<{ publishCredits: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount: number; reason?: string }) => fn(userId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.entitlement(userId) });
      qc.invalidateQueries({ queryKey: userKeys.publishHistory(userId) });
      qc.invalidateQueries({ queryKey: userKeys.detail(userId) });
    },
  });
}

export const useGrantCredits = (userId: string) =>
  useCreditMutation(userId, entitlementApi.grantCredits);

export const useRevokeCredits = (userId: string) =>
  useCreditMutation(userId, entitlementApi.revokeCredits);
